import { useState, useRef, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { Download, Loader2, X, FileSpreadsheet } from "lucide-react";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { API, apiGet } from "../api";

export const PriceFileGenerator = () => {
  const [supplier, setSupplier] = useState("all");
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [done, setDone] = useState(false);
  const [carryForward, setCarryForward] = useState(false);
  const [fmt, setFmt] = useState("csv"); // csv | xlsx
  const abortRef = useRef(null);

  // Source supplier list from the actual price files (not invoices) so users
  // can only pick suppliers that have data to export.
  useEffect(() => {
    let cancelled = false;
    apiGet("/price-files/summary").then((res) => {
      if (cancelled || !Array.isArray(res)) return;
      const unique = [...new Set(res.map((p) => p.supplier).filter(Boolean))].sort();
      setSuppliers(unique);
    });
    return () => { cancelled = true; };
  }, []);

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const downloadExport = useCallback(async () => {
    try {
      setLoading(true);
      setDone(false);
      setSummary(null);
      abortRef.current = new AbortController();

      const url = `${API}/reports/price-files-export?supplier=${encodeURIComponent(supplier)}&carry_forward=${carryForward}&fmt=${fmt}`;
      const response = await fetch(url, { method: "GET", signal: abortRef.current.signal });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const summaryHeader = response.headers.get("X-Report-Summary");
      let parsedSummary = null;
      if (summaryHeader) {
        try { parsedSummary = JSON.parse(summaryHeader); } catch { /* ignore */ }
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const dateStr = new Date().toISOString().split("T")[0];
      const safeSupplier = supplier === "all" ? "all_suppliers" : supplier.replace(/[^A-Za-z0-9_-]+/g, "_");
      const ext = fmt === "xlsx" ? "xlsx" : "csv";
      a.href = downloadUrl;
      a.download = `price_file_export_${safeSupplier}${carryForward ? "_carried" : ""}_${dateStr}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      setSummary(parsedSummary);
      setDone(true);
      toast.success("Price file export downloaded");
    } catch (error) {
      if (error.name === "AbortError") {
        toast.info("Export cancelled");
      } else {
        toast.error("Failed to generate price file export");
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
    // fmt + carryForward MUST be here: without them this callback is memoised
    // against their initial values, so toggling Excel/carry-forward updates the
    // UI but the request still goes out with the stale originals.
  }, [supplier, fmt, carryForward]);

  return (
    <div data-testid="price-file-generator">
      <div className="card" style={{ marginBottom: "2rem", marginTop: "2rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>
          Price File Generator
        </h2>
        <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", opacity: 0.8, marginBottom: "1.25rem" }}>
          Export a consolidated price list. Each unique SKU is one row. Months become columns so you can compare
          unit prices across periods at a glance. When the same SKU appears in multiple price files for the same month,
          the most recently uploaded price wins.
        </p>

        <div className="form-group" style={{ marginBottom: "1.25rem", maxWidth: 420 }}>
          <label className="form-label">Supplier</label>
          <Select value={supplier} onValueChange={setSupplier}>
            <SelectTrigger
              className="form-input"
              data-testid="generator-supplier-select"
              style={{
                height: "auto",
                padding: "0.6rem 0.75rem",
                border: "1px solid rgba(96, 240, 248, 0.2)",
                background: "rgba(10, 18, 42, 0.8)",
                color: supplier !== "all" ? "var(--octopus-ice)" : "var(--octopus-voltage)",
              }}
            >
              <SelectValue placeholder="All Suppliers" />
            </SelectTrigger>
            <SelectContent
              style={{
                background: "rgba(10, 18, 42, 0.98)",
                border: "1px solid rgba(96, 240, 248, 0.2)",
                backdropFilter: "blur(12px)",
              }}
            >
              <SelectItem value="all" style={{ color: "var(--octopus-ice)", cursor: "pointer" }}>All Suppliers</SelectItem>
              {suppliers.map((s) => (
                <SelectItem key={s} value={s} style={{ color: "var(--octopus-ice)", cursor: "pointer" }}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Carry-forward (rolling price) toggle */}
        <label style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem", marginBottom: "1rem", cursor: "pointer", maxWidth: 560 }}>
          <input type="checkbox" checked={carryForward} onChange={(e) => setCarryForward(e.target.checked)}
            data-testid="generator-carryforward" style={{ marginTop: "0.2rem" }} />
          <span style={{ fontSize: "0.9rem", color: "var(--octopus-ice)" }}>
            Carry forward gaps (rolling price)
            <span style={{ display: "block", fontSize: "0.75rem", color: "var(--octopus-voltage)", opacity: 0.7, marginTop: "0.15rem" }}>
              Fill months with no price file using the last known price, held until a newer price supersedes it — matching how invoices are now priced. No backfill before a SKU's first price. In Excel, carried cells are marked amber italic.
            </span>
          </span>
        </label>

        {/* Format selector */}
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "1.25rem" }}>
          <span style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)" }}>Format:</span>
          {[["csv", "CSV"], ["xlsx", "Excel"]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFmt(k)}
              className={fmt === k ? "btn btn-primary" : "btn btn-secondary"}
              style={{ padding: "0.35rem 0.9rem", fontSize: "0.8rem" }} data-testid={`generator-fmt-${k}`}>
              {label}
            </button>
          ))}
        </div>

        <Button
          data-testid="generator-download-btn"
          onClick={downloadExport}
          disabled={loading}
          className="btn btn-primary"
          style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
        >
          {loading ? (
            <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Generating Export...</>
          ) : (
            <><Download size={16} /> Download Price File ({fmt === "xlsx" ? "Excel" : "CSV"})</>
          )}
        </Button>

        {loading && (
          <Button
            onClick={handleCancel}
            variant="outline"
            size="sm"
            data-testid="generator-cancel-btn"
            style={{ marginLeft: "0.75rem", borderColor: "var(--octopus-pink)", color: "var(--octopus-pink)" }}
          >
            <X size={14} style={{ marginRight: "0.4rem" }} /> Cancel
          </Button>
        )}
      </div>

      {loading && (
        <div className="card" style={{ marginBottom: "2rem", textAlign: "center", padding: "2rem" }}>
          <Loader2 size={28} style={{ color: "var(--octopus-voltage)", animation: "spin 1s linear infinite", marginBottom: "0.75rem" }} />
          <p style={{ color: "var(--octopus-ice)", fontWeight: "600", fontSize: "0.95rem", marginBottom: "0.5rem" }}>
            Pivoting price files across months...
          </p>
          <p style={{ color: "var(--octopus-voltage)", fontSize: "0.8rem", opacity: 0.7 }}>
            This consolidates every uploaded price file into a single CSV. Large supplier catalogues may take a moment.
          </p>
          <div
            style={{
              width: "100%",
              maxWidth: 320,
              margin: "1rem auto 0",
              height: 4,
              borderRadius: 2,
              background: "rgba(96, 240, 248, 0.1)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                borderRadius: 2,
                background: "linear-gradient(90deg, var(--octopus-voltage), var(--octopus-green))",
                animation: "indeterminate 1.5s ease-in-out infinite",
                width: "40%",
              }}
            />
          </div>
        </div>
      )}

      {!loading && done && summary && (
        <div data-testid="generator-summary">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", marginBottom: "2rem" }}>
            {[
              { label: "SKU Rows", value: (summary.rows || 0).toLocaleString(), color: "var(--octopus-voltage)" },
              { label: "Months as Columns", value: (summary.months || 0).toLocaleString(), color: "var(--octopus-green)" },
              { label: "Suppliers Included", value: (summary.suppliers || 0).toLocaleString(), color: "#F050F8" },
            ].map((s) => (
              <div key={s.label} className="stat-card" style={{ padding: "1.25rem" }}>
                <div style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px", color: s.color, marginBottom: "0.5rem" }}>
                  {s.label}
                </div>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--octopus-ice)" }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div className="card" style={{ textAlign: "center", padding: "2rem" }}>
            <FileSpreadsheet size={36} style={{ color: "var(--octopus-green)", marginBottom: "0.75rem" }} />
            <p style={{ color: "var(--octopus-ice)", fontWeight: "600", fontSize: "1rem", marginBottom: "0.35rem" }}>
              Price file export downloaded
            </p>
            <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", opacity: 0.7 }}>
              {(summary.rows || 0).toLocaleString()} unique SKUs across {summary.months || 0} months saved to your downloads folder.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceFileGenerator;
